/**
 * Utility functions for merging resource columns with conflict resolution
 * Used in Query Builder to display available columns from multiple resources
 */

/**
 * Interface for resource column data
 */
export interface ResourceColumnInfo {
  resourceId: number;
  resourceCode: string;
  columns: string[];
  isBaseResource?: boolean;
}

/**
 * Merges columns from base and additional resources with conflict resolution
 * 
 * Rules:
 * - Base resource columns NEVER get prefixed (always keep original names)
 * - Additional/Aggregated resource columns get prefixed ONLY when there's a name conflict
 * - Prefix format: {resourceCode}_{columnName}
 * - Returns a flat list of column names
 * 
 * @param baseColumns - Array of column names from the base resource
 * @param additionalResourcesData - Array of objects containing resource info and their columns
 * @param baseResourceCode - Optional code for the base resource (for logging/debugging)
 * @returns Array of merged column names with prefixes applied where needed
 * 
 * @example
 * const baseColumns = ['id', 'name', 'status'];
 * const additionalResources = [
 *   { resourceId: 2, resourceCode: 'RES_002', columns: ['id', 'description', 'type'] },
 *   { resourceId: 3, resourceCode: 'RES_003', columns: ['type', 'category'] }
 * ];
 * 
 * const result = mergeResourceColumns(baseColumns, additionalResources);
 * // Returns: ['id', 'name', 'status', 'RES_002_id', 'description', 'RES_002_type', 'RES_003_type', 'category']
 */
export const mergeResourceColumns = (
  baseColumns: string[],
  additionalResourcesData: ResourceColumnInfo[],
  baseResourceCode?: string
): string[] => {

  const safeBaseColumns = Array.isArray(baseColumns) ? baseColumns : [];
  const safeAdditionalResources = Array.isArray(additionalResourcesData)
    ? additionalResourcesData
    : [];

  if (safeBaseColumns.length === 0) {
    return [];
  }

  if (safeAdditionalResources.length === 0) {
    return [...safeBaseColumns];
  }

  const columnNameCount = new Map<string, number>();
  const result: string[] = [];

  // First pass
  safeBaseColumns.forEach((col) => {
    const count = columnNameCount.get(col) || 0;
    columnNameCount.set(col, count + 1);
  });

  safeAdditionalResources.forEach((resource) => {
    if (Array.isArray(resource.columns)) {
      resource.columns.forEach((col) => {
        const count = columnNameCount.get(col) || 0;
        columnNameCount.set(col, count + 1);
      });
    }
  });

  // Second pass
  safeBaseColumns.forEach((col) => {
    if (!result.includes(col)) {
      result.push(col);
    }
  });

  // Third pass
  safeAdditionalResources.forEach((resource) => {
    if (!Array.isArray(resource.columns)) return;

    resource.columns.forEach((col) => {
      const hasConflict = (columnNameCount.get(col) ?? 0) > 1;

      if (hasConflict) {
        // Column name exists in multiple resources - add prefix
        const prefixedColumn = `${col}_${resource.resourceCode}`;
        if (!result.includes(prefixedColumn)) {
          result.push(prefixedColumn);
        }
      } else {
        // No conflict - use original column name
        if (!result.includes(col)) {
          result.push(col);
        }
      }
    });
  });

  return result;
};

/**
 * Extracts column names from resource column properties
 * Filters only active columns
 * 
 * @param resourceColumnProperties - Resource column properties object
 * @returns Array of active column names
 */
export const extractActiveColumns = (resourceColumnProperties: any): string[] => {
  if (!resourceColumnProperties?.resource_columns) {
    return [];
  }

  return resourceColumnProperties.resource_columns
    .filter((col: any) => col.is_active !== false)
    .map((col: any) => col.column_name)
    .filter((name: string) => name && name.trim() !== '');
};

/**
 * Helper function to build ResourceColumnInfo from additional resource data
 * Used in AdditionalResource component
 *
 * @param additionalResourceData - Array of additional resource data from formData
 * @param allResourcesData - All resources data to get resource codes
 * @param resourceColumnsData - Resource columns data fetched from API
 * @returns Array of ResourceColumnInfo objects
 */
export const buildAdditionalResourceColumnInfo = (
  additionalResourceData: any[],
  allResourcesData: any[],
  resourceColumnsData: any[]
): ResourceColumnInfo[] => {
  if (!additionalResourceData || additionalResourceData.length === 0) {
    return [];
  }

  const mappedData: (ResourceColumnInfo | null)[] = additionalResourceData
    .map((additionalRes) => {
      const resourceId = additionalRes.resource_id;
      if (!resourceId) return null;

      // Find resource to get code
      const resource = allResourcesData.find((r) => r.id === resourceId);
      if (!resource) return null;

      // Find columns for this resource
      const resourceColData = resourceColumnsData.find(
        (rc) => rc.resource_id === resourceId
      );
      if (!resourceColData?.resource_columns) return null;

      const columns = extractActiveColumns({ resource_columns: resourceColData.resource_columns });

      return {
        resourceId,
        resourceCode: resource?.resource_prefix || "",
        columns,
        isBaseResource: false,
      };
    });

  return mappedData.filter((item): item is ResourceColumnInfo => item !== null);
};

/**
 * Interface for structured column data with metadata
 */
export interface StructuredColumnData {
  type: 'simple' | 'structured';
  // For backward compatibility - flat array of all columns
  flatColumns: string[];
  // Structured data for tab display
  baseResource?: {
    columns: string[];
  };
  additionalResources?: Array<{
    resourceId: number;
    resourceName: string;
    resourceCode: string;
    columns: Array<{
      originalName: string;
      displayName: string; // Name with suffix if conflict exists
      hasConflict: boolean;
    }>;
  }>;
}

/**
 * Merges columns with metadata for tab-based display
 * Returns structured data that includes conflict information for each column
 *
 * @param baseColumns - Array of column names from the base resource
 * @param additionalResourcesData - Array of objects containing resource info and their columns
 * @param allResourcesData - All resources data to get resource names
 * @returns StructuredColumnData object with metadata
 */
export const mergeResourceColumnsWithMetadata = (
  baseColumns: string[],
  additionalResourcesData: ResourceColumnInfo[],
  allResourcesData: any[]
): StructuredColumnData => {
  // If no additional resources, return simple structure for backward compatibility
  if (!additionalResourcesData || additionalResourcesData.length === 0) {
    return {
      type: 'simple',
      flatColumns: [...baseColumns],
    };
  }

  // Track all column names to detect conflicts
  const columnNameCount = new Map<string, number>();
  const flatColumns: string[] = [];

  // First pass: Count occurrences of each column name across all resources
  baseColumns.forEach((col) => {
    const count = columnNameCount.get(col) || 0;
    columnNameCount.set(col, count + 1);
  });

  additionalResourcesData.forEach((resource) => {
    if (resource.columns && Array.isArray(resource.columns)) {
      resource.columns.forEach((col) => {
        const count = columnNameCount.get(col) || 0;
        columnNameCount.set(col, count + 1);
      });
    }
  });

  // Second pass: Build flat columns array (for backward compatibility)
  baseColumns.forEach((col) => {
    if (!flatColumns.includes(col)) {
      flatColumns.push(col);
    }
  });

  additionalResourcesData.forEach((resource) => {
    if (!resource.columns || !Array.isArray(resource.columns)) {
      return;
    }

    resource.columns.forEach((col) => {
      const hasConflict = columnNameCount.get(col)! > 1;

      if (hasConflict) {
        const prefixedColumn = `${col}_${resource.resourceCode}`;
        if (!flatColumns.includes(prefixedColumn)) {
          flatColumns.push(prefixedColumn);
        }
      } else {
        if (!flatColumns.includes(col)) {
          flatColumns.push(col);
        }
      }
    });
  });

  // Third pass: Build structured data for tab display
  const additionalResources = additionalResourcesData.map((resource) => {
    const resourceInfo = allResourcesData.find((r) => r.id === resource.resourceId);
    const resourceName = resourceInfo?.resource_name || `Resource ${resource.resourceId}`;

    const columns = (resource.columns || []).map((col) => {
      const hasConflict = columnNameCount.get(col)! > 1;
      return {
        originalName: col,
        displayName: hasConflict ? `${col}_${resource.resourceCode}` : col,
        hasConflict,
      };
    });

    return {
      resourceId: resource.resourceId,
      resourceName,
      resourceCode: resource.resourceCode,
      columns,
    };
  });

  return {
    type: 'structured',
    flatColumns,
    baseResource: {
      columns: [...baseColumns],
    },
    additionalResources,
  };
};

